import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  type DocumentData,
  type Timestamp,
} from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import { db, storage } from './firebase';
import { ensureCrmStorageAccessBeforeUpload } from './crmStorageAccess';
import type { ScenarioLine, ScenarioOutflowAttachment } from './cashFlowScenario';
import {
  assertCapexLineAttachmentFile,
  capexLineAttachmentContentType,
  capitalPlanPath,
  extractMentionUserIds,
  isCapitalPlanStatus,
  listCapitalPlanNotifyUids,
  parseScenarioLines,
  serializeScenarioLines,
  type CapitalPlan,
  type CapitalPlanComment,
  type CapitalPlanStatus,
} from './capitalPlans';

export { listCapitalPlanNotifyUids };

const COLLECTION = 'capitalPlans';

function tsToDate(value: unknown): Date {
  if (value instanceof Date) return value;
  if (value && typeof value === 'object' && 'toDate' in value) {
    return (value as Timestamp).toDate();
  }
  return new Date();
}

function mapPlan(id: string, data: Record<string, unknown>): CapitalPlan {
  const status = isCapitalPlanStatus(data.status) ? data.status : 'draft';
  return {
    id,
    title: String(data.title || 'Untitled plan'),
    why: String(data.why || ''),
    status,
    lines: parseScenarioLines(data.lines),
    createdByUserId: String(data.createdByUserId || ''),
    createdByName: String(data.createdByName || ''),
    updatedByUserId: String(data.updatedByUserId || ''),
    updatedByName: String(data.updatedByName || ''),
    createdAt: tsToDate(data.createdAt),
    updatedAt: tsToDate(data.updatedAt),
    decidedByUserId: data.decidedByUserId ? String(data.decidedByUserId) : undefined,
    decidedByName: data.decidedByName ? String(data.decidedByName) : undefined,
    decidedAt: data.decidedAt ? tsToDate(data.decidedAt) : undefined,
    decisionNote: data.decisionNote ? String(data.decisionNote) : undefined,
  };
}

export function subscribeCapitalPlans(
  onNext: (plans: CapitalPlan[]) => void,
  onError?: (err: Error) => void,
): () => void {
  const q = query(collection(db, COLLECTION), orderBy('updatedAt', 'desc'));
  return onSnapshot(
    q,
    (snap) => {
      onNext(snap.docs.map((d) => mapPlan(d.id, d.data() as Record<string, unknown>)));
    },
    (err) => onError?.(err),
  );
}

export function subscribeCapitalPlan(
  planId: string,
  onNext: (plan: CapitalPlan | null) => void,
  onError?: (err: Error) => void,
): () => void {
  return onSnapshot(
    doc(db, COLLECTION, planId),
    (snap) => {
      onNext(snap.exists() ? mapPlan(snap.id, snap.data() as Record<string, unknown>) : null);
    },
    (err) => onError?.(err),
  );
}

export function subscribeCapitalPlanComments(
  planId: string,
  onNext: (comments: CapitalPlanComment[]) => void,
  onError?: (err: Error) => void,
): () => void {
  const q = query(collection(db, COLLECTION, planId, 'comments'), orderBy('createdAt', 'asc'));
  return onSnapshot(
    q,
    (snap) => {
      onNext(
        snap.docs.map((d) => {
          const data = d.data() as Record<string, unknown>;
          return {
            id: d.id,
            body: String(data.body || ''),
            bodyHtml: typeof data.bodyHtml === 'string' && data.bodyHtml ? data.bodyHtml : undefined,
            authorUserId: String(data.authorUserId || ''),
            authorName: String(data.authorName || ''),
            createdAt: tsToDate(data.createdAt),
            mentionUserIds: Array.isArray(data.mentionUserIds)
              ? data.mentionUserIds.map(String)
              : [],
          };
        }),
      );
    },
    (err) => onError?.(err),
  );
}

export async function createCapitalPlan(args: {
  title: string;
  why: string;
  lines: ScenarioLine[];
  actorUserId: string;
  actorName: string;
  share?: boolean;
}): Promise<string> {
  const ref = await addDoc(collection(db, COLLECTION), {
    title: args.title.trim() || 'Untitled plan',
    why: args.why.trim(),
    status: args.share ? 'shared' : 'draft',
    lines: serializeScenarioLines(args.lines),
    createdByUserId: args.actorUserId,
    createdByName: args.actorName,
    updatedByUserId: args.actorUserId,
    updatedByName: args.actorName,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateCapitalPlan(args: {
  planId: string;
  title?: string;
  why?: string;
  lines?: ScenarioLine[];
  actorUserId: string;
  actorName: string;
}): Promise<void> {
  const patch: Record<string, unknown> = {
    updatedByUserId: args.actorUserId,
    updatedByName: args.actorName,
    updatedAt: serverTimestamp(),
  };
  if (args.title != null) patch.title = args.title.trim() || 'Untitled plan';
  if (args.why != null) patch.why = args.why.trim();
  if (args.lines) patch.lines = serializeScenarioLines(args.lines);
  await updateDoc(doc(db, COLLECTION, args.planId), patch as DocumentData);
}

export async function updateCapitalPlanStatus(args: {
  planId: string;
  status: CapitalPlanStatus;
  actorUserId: string;
  actorName: string;
  decisionNote?: string;
}): Promise<void> {
  const decided = args.status === 'approved' || args.status === 'deferred' || args.status === 'declined';
  await updateDoc(doc(db, COLLECTION, args.planId), {
    status: args.status,
    updatedByUserId: args.actorUserId,
    updatedByName: args.actorName,
    updatedAt: serverTimestamp(),
    ...(decided
      ? {
          decidedByUserId: args.actorUserId,
          decidedByName: args.actorName,
          decidedAt: serverTimestamp(),
          decisionNote: (args.decisionNote || '').trim(),
        }
      : {}),
  });
}

export async function deleteCapitalPlan(planId: string): Promise<void> {
  const commentsSnap = await getDocs(collection(db, COLLECTION, planId, 'comments'));
  await Promise.all(commentsSnap.docs.map((row) => deleteDoc(row.ref)));
  await deleteDoc(doc(db, COLLECTION, planId));
}

export async function addCapitalPlanComment(args: {
  planId: string;
  body: string;
  bodyHtml?: string;
  mentionUserIds?: string[];
  actorUserId: string;
  actorName: string;
  mentionCandidates: Array<{ uid: string; displayName: string }>;
}): Promise<string> {
  const body = args.body.trim();
  if (!body) throw new Error('Comment is empty');
  const mentionUserIds = args.mentionUserIds?.length
    ? Array.from(new Set(args.mentionUserIds))
    : extractMentionUserIds(body, args.mentionCandidates);
  const ref = await addDoc(collection(db, COLLECTION, args.planId, 'comments'), {
    body,
    ...(args.bodyHtml ? { bodyHtml: args.bodyHtml } : {}),
    authorUserId: args.actorUserId,
    authorName: args.actorName,
    mentionUserIds,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function notifyCapitalPlanEvent(args: {
  userIds: string[];
  title: string;
  message: string;
  planId: string;
  actorUserId: string;
}): Promise<void> {
  const unique = Array.from(new Set(args.userIds)).filter((id) => id && id !== args.actorUserId);
  await Promise.all(
    unique.map((userId) =>
      addDoc(collection(db, 'userNotifications'), {
        userId,
        type: 'capital_plan',
        title: args.title,
        message: args.message,
        read: false,
        isPriority: true,
        sourceId: args.planId,
        link: capitalPlanPath(args.planId),
        createdAt: serverTimestamp(),
      }),
    ),
  );
}

export async function uploadCapitalPlanLineAttachment(args: {
  planId: string;
  lineId: string;
  file: File;
}): Promise<ScenarioOutflowAttachment> {
  assertCapexLineAttachmentFile(args.file);
  await ensureCrmStorageAccessBeforeUpload(true);
  const safeName = args.file.name.replace(/[^\w.-]+/g, '_').slice(0, 120) || 'file';
  const storagePath = `capital-plans/${args.planId}/lines/${args.lineId}/${Date.now()}_${safeName}`;
  const ref = storageRef(storage, storagePath);
  const contentType = capexLineAttachmentContentType(args.file);
  await uploadBytes(ref, args.file, { contentType });
  const url = await getDownloadURL(ref);
  return {
    id: `file-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    name: args.file.name,
    storagePath,
    contentType: contentType === 'application/octet-stream' ? args.file.type || undefined : contentType,
    size: args.file.size,
    url,
  };
}

export async function deleteCapitalPlanLineAttachment(storagePath: string): Promise<void> {
  const path = storagePath.trim();
  if (!path) return;
  await ensureCrmStorageAccessBeforeUpload(true);
  try {
    await deleteObject(storageRef(storage, path));
  } catch (err) {
    const code = err && typeof err === 'object' && 'code' in err ? String((err as { code?: string }).code) : '';
    if (code !== 'storage/object-not-found') throw err;
  }
}

/** Used when creating a plan with a known id (tests / Save as overwrite). */
export async function setCapitalPlanDoc(planId: string, data: Record<string, unknown>): Promise<void> {
  await setDoc(doc(db, COLLECTION, planId), data, { merge: true });
}
